/**
 * Chart Color Utilities
 *
 * Centralized color mapping for all chart libraries (Recharts, Nivo, Victory, etc.)
 * Uses CSS variables from src/index.css for design system consistency.
 *
 * **Why centralized?**
 * - Single source of truth for chart colors
 * - Automatic dark mode support (CSS variables update)
 * - Easy to change colors across all charts
 * - Consistent with design system tokens
 *
 * **Usage Examples:**
 *
 * @example
 * // Recharts with category colors
 * import { chartColors, getCategoryColor } from '@/utils/chartColors';
 *
 * const data = [
 *   { category: 'food', value: 100 },
 *   { category: 'health', value: 50 },
 * ];
 *
 * <Pie data={data}>
 *   {data.map((entry, index) => (
 *     <Cell key={index} fill={getCategoryColor(entry.category)} />
 *   ))}
 * </Pie>
 *
 * @example
 * // Nivo with theme
 * import { nivoTheme, chartColors } from '@/utils/chartColors';
 *
 * <ResponsivePie
 *   data={data}
 *   theme={nivoTheme}
 *   colors={chartColors.series}
 * />
 *
 * @example
 * // Multi-series chart
 * import { getSeriesColor } from '@/utils/chartColors';
 *
 * <Line data={series1} stroke={getSeriesColor(0)} />
 * <Line data={series2} stroke={getSeriesColor(1)} />
 *
 * @see {@link file://./../../docs/design-system/CHARTS.md} for detailed integration guide
 */

export const chartColors = {
  // Category colors - primary use case for receipt categorization
  categories: {
    food: "hsl(var(--category-food))",
    household: "hsl(var(--category-household))",
    health: "hsl(var(--category-health))",
    personal: "hsl(var(--category-personal))",
    other: "hsl(var(--category-other))",
  },

  // Primary/Secondary colors for main data series
  primary: "hsl(var(--primary))",
  secondary: "hsl(var(--secondary))",

  // Status/Feedback colors
  destructive: "hsl(var(--destructive))",
  muted: "hsl(var(--muted))",

  // Trend indicators
  trend: {
    up: "hsl(var(--category-health))", // Green for positive trends
    down: "hsl(var(--destructive))", // Red for negative trends
    neutral: "hsl(var(--muted-foreground))", // Gray for neutral
  },

  // Chart structure elements (axes, grids, labels)
  structure: {
    grid: "hsl(var(--border))",
    axis: "hsl(var(--muted-foreground))",
    text: "hsl(var(--foreground))",
    background: "hsl(var(--background))",
  },

  // Multi-series data (for multiple lines/bars in same chart)
  series: [
    "hsl(var(--primary))",
    "hsl(var(--category-food))",
    "hsl(var(--category-household))",
    "hsl(var(--category-health))",
    "hsl(var(--category-personal))",
    "hsl(var(--category-other))",
  ],
} as const;

/**
 * Get category color by name
 * Useful for dynamic category mapping in charts
 */
export const getCategoryColor = (
  category: keyof typeof chartColors.categories
): string => {
  return chartColors.categories[category];
};

/**
 * Get series color by index
 * Useful for multi-series charts (e.g., comparing stores)
 */
export const getSeriesColor = (index: number): string => {
  return chartColors.series[index % chartColors.series.length];
};

/**
 * Nivo theme configuration
 * Use this as the theme prop for all Nivo charts
 */
export const nivoTheme = {
  background: "transparent",
  textColor: "hsl(var(--foreground))",
  fontSize: 12,
  fontFamily: "Inter, sans-serif",

  axis: {
    domain: {
      line: {
        stroke: "hsl(var(--border))",
        strokeWidth: 1,
      },
    },
    ticks: {
      line: {
        stroke: "hsl(var(--border))",
        strokeWidth: 1,
      },
      text: {
        fill: "hsl(var(--muted-foreground))",
        fontSize: 11,
      },
    },
    legend: {
      text: {
        fill: "hsl(var(--foreground))",
        fontSize: 12,
        fontWeight: 600,
      },
    },
  },

  grid: {
    line: {
      stroke: "hsl(var(--border))",
      strokeWidth: 1,
    },
  },

  legends: {
    text: {
      fill: "hsl(var(--foreground))",
      fontSize: 12,
    },
  },

  tooltip: {
    container: {
      background: "hsl(var(--popover))",
      color: "hsl(var(--popover-foreground))",
      fontSize: 12,
      borderRadius: "6px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      padding: "8px 12px",
    },
  },
};

/**
 * Victory theme configuration
 * Use this as the theme prop for all Victory charts
 */
export const victoryTheme = {
  axis: {
    style: {
      axis: { stroke: "hsl(var(--border))" },
      axisLabel: {
        fill: "hsl(var(--foreground))",
        fontSize: 12,
        padding: 30,
      },
      grid: { stroke: "hsl(var(--border))", strokeWidth: 1 },
      ticks: { stroke: "hsl(var(--border))", size: 5 },
      tickLabels: {
        fill: "hsl(var(--muted-foreground))",
        fontSize: 11,
      },
    },
  },
  legend: {
    style: {
      labels: { fill: "hsl(var(--foreground))", fontSize: 12 },
    },
  },
};
